import { IMessageTopicFields } from '@/contentful/types';

import { MessageLanguage } from './Language';

export interface Incident {
  incidentId: string;
  ticketNumber?: string;
  title: string;
  description: string;
  resolution?: string;
  createdon: string;
  modifiedon?: string;
  language?: MessageLanguage;
  classification?: IncidentClassification;
  subClassification?: IncidentSubClassification;
  stateCode?: MessageState;
  statusCode: MessageStatus;
  contactRentalContract?: ContactRentalContract;
  incidentHandled?: boolean;
  workOrders: WorkOrder[];
  /** Id of the original sender */
  customerContactId: string;
}

export interface ContactRentalContract {
  rentalContractId?: string;
  name?: string;
  startDate?: string;
  endDate?: string;
  contractType?: string;
}

export interface WorkOrder {
  title: string;
  status: WorkOrderStatus;
  supportContactType: SupportContactType;
  solutionDescription?: string;
}

export enum SupportContactType {
  PARTNER = 'PARTNER',
  SATO_HOUSE_EXPERT = 'SATO_HOUSE_EXPERT',
}

export enum WorkOrderStatus {
  OPEN = 'OPEN',
  OPEN_AND_SCHEDULED = 'OPEN_AND_SCHEDULED',
  OPEN_AND_IN_PROGRESS = 'OPEN_AND_IN_PROGRESS',
  OPEN_AND_COMPLETED = 'OPEN_AND_COMPLETED',
  CLOSED_AND_PUBLISHED = 'CLOSED_AND_PUBLISHED',
  CLOSED_AND_CANCELED = 'CLOSED_AND_CANCELED',
}

export enum MessageState {
  ACTIVE = 'ACTIVE',
  RESOLVED = 'RESOLVED',
  REJECTED = 'REJECTED',
}

export enum MessageStatus {
  OPEN = 'OPEN',
  ONHOLD = 'ONHOLD',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_PARTNER = 'WAITING_FOR_PARTNER',
  WAITING_FOR_DETAILS = 'WAITING_FOR_DETAILS',
  COMPLETED = 'COMPLETED',
  REJECTED = 'REJECTED',
  GIVEN_INFORMATION = 'GIVEN_INFORMATION',
  COMBINED = 'COMBINED',
}

export enum IncidentClassification {
  TERMINATION = 'TERMINATION',
  SALES = 'SALES',
  SALES_CONTACTS = 'SALES_CONTACTS',
  RENTALPAYMENT_STATUS = 'RENTALPAYMENT_STATUS',
  DEFECT_REPORT = 'DEFECT_REPORT',
  DEFECT_REPORT_PUBLIC = 'DEFECT_REPORT_PUBLIC',
  KEYS = 'KEYS',
  CONTRACTS = 'CONTRACTS',
  APARTMENT_CONDITION = 'APARTMENT_CONDITION',
  SUPPORT = 'SUPPORT',
  CREATED_BY_SATO = 'CREATED_BY_SATO',
  FACILITY_RESERVATION = 'FACILITY_RESERVATION',
}

export enum IncidentSubClassification {
  TERMINATION = 'TERMINATION',
  CANCEL_TERMINATION = 'CANCEL_TERMINATION',
  MANUAL_TERMINATION = 'MANUAL_TERMINATION',
  MANUAL_PARTY_CHANGE = 'MANUAL_PARTY_CHANGE',
  DELAYING_RENT_PAYMENT = 'DELAYING_RENT_PAYMENT',
  PAYMENT_MEMORY = 'PAYMENT_MEMORY',
  RENTALPAYMENT = 'RENTALPAYMENT',
  OTHER_QUESTION = 'OTHER_QUESTION',
  PEST_CONTROL = 'PEST_CONTROL',
  APARTMENT_TEMPERATURE = 'APARTMENT_TEMPERATURE',
  HOME_APPLIANCE = 'HOME_APPLIANCE',
  INDOOR_AIR_QUALITY = 'INDOOR_AIR_QUALITY',
  SMALL_DEFECT = 'SMALL_DEFECT',
  ELEVATOR = 'ELEVATOR',
  OUTDOOR_MAINTENANCE = 'OUTDOOR_MAINTENANCE',
  GARBAGE_AREA_MAINTENANCE = 'GARBAGE_AREA_MAINTENANCE',
  GARBAGE_MAINTENANCE = 'GARBAGE_MAINTENANCE',
  CLEANING = 'CLEANING',
  CLEANING_REFUND = 'CLEANING_REFUND',
  PARKING_ISSUE = 'PARKING_ISSUE',
  OTHER_APARTMENT_DEFECT = 'OTHER_APARTMENT_DEFECT',
  ELECTRICITY_DEFECT = 'ELECTRICITY_DEFECT',
  PUBLIC_PLACES = 'PUBLIC_PLACES',
  RENT_GUARANTEE_RETURN = 'RENT_GUARANTEE_RETURN',
  ORDER_NEW_KEY = 'ORDER_NEW_KEY',
  ORDER_NEW_COMMON_SPACES_KEY = 'ORDER_NEW_COMMON_SPACES_KEY',
  QUESTION_FOR_PAYMENT = 'QUESTION_FOR_PAYMENT',
  OTHER_PUBLIC_DEFECT = 'OTHER_PUBLIC_DEFECT',
  OTHER_DEFECT = 'OTHER_DEFECT',
  LOST_KEY = 'LOST_KEY',
  OTHER_REASON = 'OTHER_REASON',
  NEIGHBOUR_MENTOR = 'NEIGHBOUR_MENTOR',
  RENOVATE = 'RENOVATE',
  PAINTING = 'PAINTING',
  RECLAMATION = 'RECLAMATION',
  DISORDER = 'DISORDER',
  QUESTION_FOR_CONTRACT = 'QUESTION_FOR_CONTRACT',
  NEW_PARTY = 'NEW_PARTY',
  CONTRACT_TRANSFER = 'CONTRACT_TRANSFER',
  NEW_RESIDENT = 'NEW_RESIDENT',
  NAME_UPDATE = 'NAME_UPDATE',
  PAID_HOUSE_EXPERT_WORK_1_HOUR = 'PAID_HOUSE_EXPERT_WORK_1_HOUR',
  PARKING_SPACE_RENTING = 'PARKING_SPACE_RENTING',
  SAUNA_RESERVATION = 'SAUNA_RESERVATION',
}
export type MessageTopicId = IMessageTopicFields['id'];

const messageTopicIdList: Array<MessageTopicId> = [
  'CONTRACTS',
  'QUESTION_FOR_CONTRACT',
  'NEW_PARTY',
  'NEW_RESIDENT',
  'CONTRACT_TRANSFER',
  'PAYMENTS',
  'RENTAL_PAYMENT',
  'PAYMENT_REMINDER',
  'RENT_GUARANTEE_RETURN',
  'RENT_GUARANTEE_RETURN_MOVED_AWAY',
  'RENT_GUARANTEE_RETURN_WHILE_TENANT',
  'QUESTION_FOR_PAYMENT',
  'KEYS',
  'ORDER_NEW_KEY',
  'LOST_KEY',
  'KEY_RETURNS_AND_RETRIEVALS',
  'PARKING',
  'PARKING_RENT',
  'PARKING_TERMINATE',
  'PARKING_OTHER',
  'RENOVATION',
  'PAINTING',
  'RENOVATE',
  'DEFECT',
  'APARTMENT_DEFECT',
  'APARTMENT_DEFECT_WATER_DAMAGE',
  'APARTMENT_DEFECT_APPLIANCE',
  'APARTMENT_DEFECT_TEMPERATURE',
  'APARTMENT_DEFECT_VENTILATION',
  'APARTMENT_DEFECT_MINOR_REPAIR_REQUEST',
  'APARTMENT_DEFECT_PESTS',
  'APARTMENT_DEFECT_ELECTRICAL',
  'APARTMENT_DEFECT_INTERNET',
  'APARTMENT_DEFECT_NAME_UPDATE',
  'APARTMENT_DEFECT_PAID_HOUSE_EXPERT_WORK',
  'APARTMENT_DEFECT_OTHER',
  'BUILDING_DEFECT',
  'BUILDING_DEFECT_PESTS',
  'BUILDING_DEFECT_ELEVATOR',
  'BUILDING_DEFECT_YARD',
  'BUILDING_DEFECT_CLEANLINESS',
  'BUILDING_DEFECT_WASTE',
  'BUILDING_DEFECT_WASTE_COLLECTION',
  'BUILDING_DEFECT_PARKING',
  'BUILDING_DEFECT_COMMON_SPACES',
  'BUILDING_DEFECT_OTHER',
  'SAUNA',
  'RESERVE_SHIFT',
  'MESSY_SAUNA',
  'SAUNA_DEFECT',
  'SAUNA_RESERVATION',
  'DISTURBANCE',
  'NEIGHBOUR_DISTURBANCE',
  'COMMON_DISTURBANCE',
  'OTHER_DISTURBANCE',
  'OTHER_REASON',
];

export const isMessageTopicId = (topicId: string): topicId is MessageTopicId =>
  messageTopicIdList.some((x) => topicId === x);
